import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useClient } from '../context/ClientContext';
import { formatET } from '../lib/timezone';
import { Inbox, CheckCircle, ArrowRight, Calendar, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITIES = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600' },
  high: { label: 'High', color: 'bg-amber-100 text-amber-600' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-600' }
};

const ISSUE_STYLES = {
  approved_with_edits: { label: 'Approved with edits', style: 'bg-blue-100 text-blue-700' },
  changes_requested: { label: 'Changes requested', style: 'bg-orange-100 text-orange-700' },
  do_not_run: { label: 'Do not run', style: 'bg-red-100 text-red-700' },
  missed: { label: 'Missed', style: 'bg-red-100 text-red-700' },
  failed: { label: 'Publish failed', style: 'bg-red-100 text-red-700' },
  warning: { label: 'Not publishing yet', style: 'bg-amber-100 text-amber-800' },
};

function issueKey(post) {
  if (['approved_with_edits', 'changes_requested', 'do_not_run'].includes(post.approval_status)) return post.approval_status;
  if (['missed', 'failed'].includes(post.publish_status)) return post.publish_status;
  return 'warning';
}

function issueText(post) {
  const key = issueKey(post);
  if (['approved_with_edits', 'changes_requested', 'do_not_run'].includes(key)) {
    return post.approval_note || post.client_feedback?.[post.client_feedback.length - 1]?.comment || '';
  }
  return key === 'warning' ? post.publish_warning : post.publish_error;
}

export function AgencyQueuePage() {
  const [inbox, setInbox] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const { toast } = useToast();
  const { activeClient } = useClient();
  const navigate = useNavigate();

  const loadData = async () => {
    try {
      const { data } = await axios.get(`${API}/agency/inbox`, { withCredentials: true });
      setInbox(data);
      window.dispatchEvent(new Event('inbox-updated'));
    } catch (error) {
      console.error('Failed to load inbox:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (request, message) => {
    try {
      await request();
      toast({ title: message });
      setSelectedIdea(null);
      loadData();
    } catch {
      toast({ title: 'Something went wrong, please try again', variant: 'destructive' });
    }
  };

  const actions = {
    schedule: (id) => run(() => axios.put(`${API}/posts/${id}/workflow?workflow_status=scheduled`, {}, { withCredentials: true }), 'Edits done, post is scheduled'),
    sendBack: (id) => run(() => axios.post(`${API}/agency/posts/${id}/send-back`, {}, { withCredentials: true }), 'Sent back to the client for review'),
    acknowledge: (id) => run(() => axios.post(`${API}/agency/feedback/${id}/acknowledge`, {}, { withCredentials: true }), 'Got it, this post will not be published'),
    dismiss: (id) => run(() => axios.post(`${API}/agency/posts/${id}/dismiss`, {}, { withCredentials: true }), 'Removed from Needs Action'),
    retry: (id) => run(() => axios.post(`${API}/posts/${id}/retry-publish`, {}, { withCredentials: true }), 'Retrying within 60 seconds'),
    open: (post) => {
      const y = formatET(post.scheduled_date, 'yyyy');
      const m = formatET(post.scheduled_date, 'M');
      navigate(y && m ? `/month/${y}/${m}?post=${post.id}` : '/dashboard');
    },
  };

  const handleConvertIdea = (ideaId, scheduledDate) => run(
    () => axios.post(`${API}/ideas/${ideaId}/convert${scheduledDate ? `?scheduled_date=${scheduledDate}` : ''}`, {}, { withCredentials: true }),
    'Idea converted to post!'
  );
  const handleUpdateIdeaStatus = (ideaId, status, response) => run(
    () => axios.put(`${API}/ideas/${ideaId}`, { status, agency_response: response }, { withCredentials: true }),
    'Idea updated'
  );

  const sections = inbox ? [
    { key: 'edits', title: 'Client asked for changes', hint: 'Make the edits, then schedule it or send it back to the client', posts: inbox.edits },
    { key: 'publishing', title: 'Publishing problems', hint: 'These posts can\'t go out as planned', posts: inbox.publishing },
    { key: 'do_not_run', title: 'Client said do not run', hint: 'These will not be published', posts: inbox.do_not_run },
  ] : [];

  return (
    <div className="min-h-screen page-background" data-testid="agency-queue-page">
      <Navbar />
      <InboxHero total={inbox?.total} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-6 space-y-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--brand-primary)', borderTopColor: 'transparent' }}></div>
          </div>
        ) : inbox?.total === 0 ? (
          <EmptyState icon={CheckCircle} title="You're all caught up" description="Nothing needs your attention right now. Approved posts will publish on schedule." />
        ) : (
          <>
            {sections.filter(s => s.posts?.length > 0).map(s => (
              <InboxSection key={s.key} title={s.title} hint={s.hint} count={s.posts.length} testId={`inbox-section-${s.key}`}>
                {s.posts.map(post => <InboxPostCard key={post.id} post={post} actions={actions} />)}
              </InboxSection>
            ))}
            {inbox?.ideas?.length > 0 && (
              <InboxSection title="New client ideas" hint="Ideas your client sent through the portal" count={inbox.ideas.length} testId="inbox-section-ideas">
                {inbox.ideas.map(idea => <IdeaQueueCard key={idea.id} idea={idea} onSelect={() => setSelectedIdea(idea)} />)}
              </InboxSection>
            )}
          </>
        )}
      </main>
      <IdeaDetailModal idea={selectedIdea} onClose={() => setSelectedIdea(null)} onConvert={handleConvertIdea} onUpdateStatus={handleUpdateIdeaStatus} />
    </div>
  );
}

function InboxHero({ total }) {
  return (
    <div className="py-10" style={{ background: `linear-gradient(to right, var(--brand-primary), color-mix(in srgb, var(--brand-primary) 80%, var(--brand-accent)))` }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-4">
        <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
          <Inbox className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Needs My Action
          </h1>
          <p className="text-white/70 text-sm" data-testid="inbox-total">
            {total ? `${total} item${total === 1 ? '' : 's'} waiting on you` : 'Only things that need you show up here'}
          </p>
        </div>
      </div>
    </div>
  );
}

function InboxSection({ title, hint, count, testId, children }) {
  return (
    <section data-testid={testId}>
      <div className="flex items-baseline gap-3 mb-3">
        <h2 className="text-base md:text-lg font-semibold text-[#1e293b]">{title}</h2>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#1e293b] text-white">{count}</span>
      </div>
      <p className="text-sm text-[#64748b] mb-4">{hint}</p>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function InboxPostCard({ post, actions }) {
  const key = issueKey(post);
  const issue = ISSUE_STYLES[key];
  const text = issueText(post);
  return (
    <div className="bg-white rounded-xl border border-[#e2e8f0] p-5 hover:shadow-lg transition-shadow" data-testid={`inbox-post-${post.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => actions.open(post)}>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${issue.style}`}>{issue.label}</span>
            <span className="flex items-center gap-1 text-xs text-[#94a3b8]">
              <Calendar className="w-3 h-3" />
              {post.scheduled_date ? formatET(post.scheduled_date, 'MMM d, yyyy') : 'No date'}
            </span>
          </div>
          <h3 className="font-semibold text-[#1e293b] mb-1">{post.title}</h3>
          {text && (
            <p className="text-sm text-[#475569] bg-[#f8fafc] rounded-lg px-3 py-2 mt-2" data-testid={`inbox-note-${post.id}`}>
              {['approved_with_edits', 'changes_requested', 'do_not_run'].includes(key) ? <><strong>Client says:</strong> {text}</> : text}
            </p>
          )}
        </div>
        <InboxActions post={post} issueKey={key} actions={actions} />
      </div>
    </div>
  );
}

function InboxActions({ post, issueKey: key, actions }) {
  const btn = 'rounded-lg text-xs';
  return (
    <div className="flex sm:flex-col gap-2 shrink-0 flex-wrap">
      {key === 'approved_with_edits' && (
        <Button size="sm" className={`btn-primary ${btn}`} onClick={() => actions.schedule(post.id)} data-testid={`inbox-schedule-${post.id}`}>
          Edits done, schedule it
        </Button>
      )}
      {key === 'changes_requested' && (
        <Button size="sm" className={`btn-primary ${btn}`} onClick={() => actions.sendBack(post.id)} data-testid={`inbox-send-back-${post.id}`}>
          Send back to client
        </Button>
      )}
      {key === 'do_not_run' && (
        <Button size="sm" variant="outline" className={btn} onClick={() => actions.acknowledge(post.id)} data-testid={`inbox-ack-${post.id}`}>
          Got it
        </Button>
      )}
      {key === 'failed' && (
        <Button size="sm" className={`bg-red-500 hover:bg-red-600 text-white ${btn}`} onClick={() => actions.retry(post.id)} data-testid={`inbox-retry-${post.id}`}>
          <RefreshCw className="w-3 h-3 mr-1" /> Retry
        </Button>
      )}
      <Button size="sm" variant="outline" className={btn} onClick={() => actions.open(post)} data-testid={`inbox-open-${post.id}`}>
        Open post <ArrowRight className="w-3 h-3 ml-1" />
      </Button>
      {key !== 'do_not_run' && (
        <Button size="sm" variant="ghost" className={`${btn} text-[#64748b]`} onClick={() => actions.dismiss(post.id)} data-testid={`inbox-dismiss-${post.id}`}>
          Dismiss
        </Button>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="bg-white rounded-2xl p-12 text-center" data-testid="inbox-empty">
      <Icon className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-[#1e293b] mb-2">{title}</h3>
      <p className="text-[#64748b]">{description}</p>
    </div>
  );
}

function IdeaQueueCard({ idea, onSelect }) {
  const priorityConfig = PRIORITIES[idea.priority] || PRIORITIES.normal;
  return (
    <div
      className="bg-white rounded-xl border border-[#e2e8f0] p-5 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={onSelect}
      data-testid={`idea-queue-${idea.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${priorityConfig.color}`}>
            {priorityConfig.label}
          </span>
          <h3 className="font-semibold text-[#1e293b] mt-2 mb-1">{idea.title}</h3>
          <p className="text-sm text-[#64748b] line-clamp-2">{idea.description}</p>
          <p className="text-xs text-[#94a3b8] mt-3">Submitted {formatET(idea.created_at, 'MMM d, yyyy')}</p>
        </div>
        <Button size="sm" className="btn-primary rounded-lg">
          Review <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function IdeaDetailModal({ idea, onClose, onConvert, onUpdateStatus }) {
  const [scheduledDate, setScheduledDate] = useState('');
  const [agencyResponse, setAgencyResponse] = useState('');

  if (!idea) return null;

  return (
    <Dialog open={!!idea} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]" aria-describedby="idea-detail-desc">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#1e293b]">Review Client Idea</DialogTitle>
          <p id="idea-detail-desc" className="sr-only">Review and manage client submitted idea</p>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-1 block">Title</label>
            <p className="text-[#1e293b] font-medium">{idea.title}</p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-1 block">Description</label>
            <p className="text-[#475569] text-sm">{idea.description}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-1 block">Timeframe</label>
              <p className="text-[#475569] text-sm capitalize">{idea.timeframe.replace('_', ' ')}</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-1 block">Priority</label>
              <p className="text-[#475569] text-sm capitalize">{idea.priority}</p>
            </div>
          </div>

          <hr className="border-[#e2e8f0]" />

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">
              Schedule For (optional)
            </label>
            <Input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="border-[#e2e8f0]"
            />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">
              Response to Client (optional)
            </label>
            <Textarea
              value={agencyResponse}
              onChange={(e) => setAgencyResponse(e.target.value)}
              placeholder="Add a note for the client..."
              rows={3}
              className="border-[#e2e8f0] resize-none"
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onUpdateStatus(idea.id, 'declined', agencyResponse)}
            data-testid="idea-decline-btn"
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            Decline
          </Button>
          <Button
            variant="outline"
            onClick={() => onUpdateStatus(idea.id, 'under_review', agencyResponse)}
            data-testid="idea-reviewing-btn"
            className="border-[#e2e8f0]"
          >
            Mark Reviewing
          </Button>
          <Button
            onClick={() => onConvert(idea.id, scheduledDate || null)}
            data-testid="idea-convert-btn"
            className="btn-primary"
          >
            Convert to Post
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
